import GET_WORKSPACE_BY_DOMAIN from 'lib/get-workspace-by-domain.graphql'

import { Fragment } from 'react'

import {
  checkCookies,
  getCookie,
  removeCookies,
  setCookies,
} from 'cookies-next'
import type { GetServerSideProps, NextPage } from 'next'
import { NextSeo } from 'next-seo'

import { gql } from '@apollo/client'
import Login from 'Root/Pages/Login'
import apolloClient from 'lib/apollo-client'
import { getCookieDomain, isCustomDomain } from 'lib/domain'
import { CreatorInfo, getElementPreview } from 'lib/get-element-preview'
import { getMetaTitleName } from 'lib/get-meta-title-name'
import getQueryString from 'lib/get-query-string'
import { loadCatalog } from 'lib/i18n'
import { getSubjectFromJwt } from 'lib/subject-from-jwt'
import { updateUserLanguage } from 'lib/update-user-language'
import { getValidLanguageForUser } from 'lib/utils'
import first from 'lodash/first'
import isArray from 'lodash/isArray'
import toString from 'lodash/toString'

import { additional, defaultFavicons } from '../../next-seo.config'

const GET_WORKSPACE_BY_SLUG = gql`
  query GetWorkspaceBySlug($slug: String!) {
    workspaceBySlug(slug: $slug) {
      id
      brandName
      brandLogoSmall
      hideSupportUIForConsumer
      user {
        id
        firstname
        lastname
        picture
        vanity_name
        payoff
        banner
      }
    }
  }
`

const Page: NextPage = ({ creatorInfo }: { creatorInfo: CreatorInfo }) => {
  const pageTitle = `${getMetaTitleName(creatorInfo)} – Login`

  const pageFavicon = creatorInfo?.brandLogoSmall
    ? [
        {
          rel: 'icon',
          href: creatorInfo.brandLogoSmall,
        },
      ]
    : defaultFavicons

  const additionalDefaults = creatorInfo
    ? {
        description: creatorInfo?.payoff ?? undefined,
        openGraph: {
          ...(creatorInfo?.banner && {
            images: [
              {
                height: 1200,
                url: creatorInfo.banner,
                width: 1200,
              },
            ],
          }),
        },
      }
    : { ...additional }

  return (
    <Fragment>
      <NextSeo
        openGraph={{
          title: pageTitle,
        }}
        title={pageTitle}
        additionalLinkTags={pageFavicon}
        {...additionalDefaults}
      />
      <Login creatorInfo={creatorInfo} />
    </Fragment>
  )
}

export const getServerSideProps: GetServerSideProps = async ({
  query,
  req,
  res,
}) => {
  const translation = await loadCatalog(getQueryString(query.locale))
  let accessToken = getCookie('access_token', { req, res })
  let hasAccessToken = checkCookies('access_token', { req, res })
  let elementPreviewData
  const now = new Date()

  if (Boolean(query.first_access)) {
    setCookies('gtag_need_to_track_registration', true, { req, res })

    updateUserLanguage(
      getValidLanguageForUser(getQueryString(query.locale)),
      getQueryString(query.access_token)
    )
  }

  // on the login page we have to:
  // - convert query string access_token into related cookie
  if (Boolean(query.access_token)) {
    accessToken = isArray(query.access_token)
      ? first(query.access_token)
      : query.access_token
    hasAccessToken = true

    setCookies('access_token', accessToken, {
      req,
      res,
      httpOnly: false,
      expires: new Date(now.setUTCMonth(now.getUTCMonth() + 3)),
      domain: getCookieDomain(req?.headers?.host || null),
      sameSite: 'none',
      secure: true,
    })
  }

  if (Boolean(query.workspace_role)) {
    setCookies('workspace_role', query.workspace_role, {
      req,
      res,
      httpOnly: false,
      expires: new Date(now.setUTCMonth(now.getUTCMonth() + 3)),
      domain: getCookieDomain(req?.headers?.host || null),
      sameSite: 'none',
      secure: true,
    })
  }

  if (Boolean(query.workspace_id)) {
    setCookies('workspace_id', query.workspace_id, {
      req,
      res,
      httpOnly: false,
      expires: new Date(now.setUTCMonth(now.getUTCMonth() + 3)),
      domain: getCookieDomain(req?.headers?.host || null),
      sameSite: 'none',
      secure: true,
    })
  }

  if (
    (hasAccessToken && Boolean(query.plan)) ||
    checkCookies('plan', { req, res })
  ) {
    let plan
    if (!!query.plan) {
      plan = query.plan
    } else {
      plan = getCookie('plan', { req, res })
      removeCookies('plan', { req, res })
    }
    return {
      redirect: {
        destination: `/?plan=${plan}`,
        permanent: false,
      },
    }
  }

  if (Boolean(query._ae)) {
    setCookies('additional_email_to_confirm', query._ae, {
      req,
      res,
      httpOnly: false,
      expires: new Date(now.setUTCMonth(now.getUTCMonth() + 3)),
      domain: getCookieDomain(req?.headers?.host || null),
      sameSite: 'none',
      secure: true,
    })
  }

  // - check if the cookie is valid, if it's not logout the user
  if (hasAccessToken && !Boolean(getSubjectFromJwt(accessToken))) {
    return {
      redirect: {
        destination: '/logout',
        permanent: false,
      },
    }
  }

  // - if the user is logged, redirect it to the redirection page
  if (hasAccessToken) {
    // for now redirect can be set with both a cookie or next.js useRouter returnUrl
    const getRedirect = (query) => {
      if (query.returnUrl) return query.returnUrl
      return getCookie('after_login_redirect', { req, res })
    }
    // if the user wasn't on a specific page, redirect it to the home
    let destination = getRedirect(query) || '/'
    destination = toString(
      isArray(destination) ? first(destination) : destination
    )
    // there's no need to send the user to the login
    // we can prevent the request

    // remove redirection if was defined
    removeCookies('after_login_redirect', { req, res })

    if (destination.match(/.*\/login$/)) {
      destination = '/'
    }

    return {
      redirect: {
        destination,
        permanent: false,
      },
    }
  }

  let creatorInfo = null
  let workspace = null
  const returnUrl = getQueryString(query.returnUrl)
  const hasSaveAction = /(?:\?|&)action=save(?:&|$)/.test(returnUrl)

  const host = req?.headers?.host || null
  const isWhitelabel = isCustomDomain(host)

  if (isWhitelabel) {
    const workspaceResult = await apolloClient.query({
      query: GET_WORKSPACE_BY_DOMAIN,
      variables: {
        domainName: host,
      },
    })

    const workspaceData = workspaceResult?.data?.workspace
    const brandName = workspaceData?.brandName ?? ''
    const brandLogoSmall = workspaceData?.brandLogoSmall ?? ''
    const user = workspaceData?.user ?? {}

    const {
      id = '',
      firstname = '',
      lastname = '',
      picture = '',
      vanity_name = '',
      payoff = '',
      banner = '',
    } = user

    creatorInfo = {
      id,
      name: firstname,
      firstname,
      lastname,
      picture,
      vanity_name,
      payoff,
      banner,
      brandName,
      brandLogoSmall,
      hasCustomDomain: true,
    }

    if (workspaceData?.hideSupportUIForConsumer != null) {
      workspace = {
        hideSupportUIForConsumer: workspaceData.hideSupportUIForConsumer,
      }
    }
  } else {
    if (query.returnUrl && !hasSaveAction) {
      const workspaceSlugMatch = returnUrl.match(/^\/w\/([^/?]+)/)

      if (workspaceSlugMatch) {
        const workspaceResult = await apolloClient.query({
          query: GET_WORKSPACE_BY_SLUG,
          variables: { slug: workspaceSlugMatch[1] },
        })

        const workspaceData = workspaceResult?.data?.workspaceBySlug
        if (workspaceData) {
          const brandName = workspaceData.brandName ?? ''
          const brandLogoSmall = workspaceData.brandLogoSmall ?? ''
          const user = workspaceData.user ?? {}
          const {
            id = '',
            firstname = '',
            lastname = '',
            picture = '',
            vanity_name = '',
            payoff = '',
            banner = '',
          } = user

          creatorInfo = {
            id,
            name: firstname,
            firstname,
            lastname,
            picture,
            vanity_name,
            payoff,
            banner,
            brandName,
            brandLogoSmall,
            hasCustomDomain: true,
          }

          if (workspaceData.hideSupportUIForConsumer != null) {
            workspace = {
              hideSupportUIForConsumer: workspaceData.hideSupportUIForConsumer,
            }
          }
        }
      } else {
        elementPreviewData = await getElementPreview(returnUrl)

        if (elementPreviewData?.creator) {
          creatorInfo = elementPreviewData.creator
          creatorInfo.hasCustomDomain = true
        }

        if (elementPreviewData?.creator?.workspace != null) {
          workspace = elementPreviewData.creator.workspace
        }
      }
    }
  }

  // - display the login page if both are missing
  return {
    props: {
      translation: translation,
      creatorInfo: creatorInfo,
      workspace: workspace,
    },
  }
}

export default Page
